import { formatDate, formatDateTime, formatTime } from "../lib/locale.ts";

/**
 * Renders a date/time in the app's fixed English, Latin-digit form (ISO
 * `YYYY-MM-DD` by default — see DATE POLICY in lib/locale.ts) wrapped in a
 * `<bdi dir="ltr">` so the bidi algorithm can NEVER mirror or reorder it inside
 * an RTL (Arabic/Kurdish) layout. This is the canonical way to show any date,
 * time, birth date, invoice date, appointment date, etc. across the app.
 *
 *   <DateText value={invoice.created_at} />            → 2026-06-19
 *   <DateText value={appt.start_at} withTime />        → 2026-06-19 14:30
 *   <DateText value={appt.start_at} timeOnly />        → 14:30
 *   <DateText value={d} opts={{ month: "short", day: "numeric" }} /> → 19 Jun
 *
 * `tabular-nums` keeps digits monospaced so dates line up in tables/lists.
 */
export function DateText({
	value,
	withTime = false,
	timeOnly = false,
	opts,
	className,
}: {
	value: string | number | Date | null | undefined;
	withTime?: boolean;
	timeOnly?: boolean;
	opts?: Intl.DateTimeFormatOptions;
	className?: string;
}) {
	const text = timeOnly
		? formatTime(value)
		: withTime
			? formatDateTime(value)
			: formatDate(value, undefined, opts);
	return (
		<bdi
			dir="ltr"
			className={`inline-block tabular-nums${className ? ` ${className}` : ""}`}
		>
			{text}
		</bdi>
	);
}
